import { Avatar, Box, Button, Center, Container, Divider, Flex, Grid, GridItem, Heading, Icon, Image, Menu, MenuButton, MenuItem, MenuList, Skeleton, Text, useDisclosure } from '@chakra-ui/react';
import _ from 'lodash';
import { useEffect, useMemo } from 'react';
import { BiChevronDown, BiSearch } from 'react-icons/bi';
import { BsCart4 } from 'react-icons/bs';
import { FaCrown } from 'react-icons/fa';
import { HiOutlineMapPin } from 'react-icons/hi2';
import { LuCalendarSearch } from 'react-icons/lu';
import { PiImageBroken, PiShoppingBag } from 'react-icons/pi';
import { TbShoppingBagX } from 'react-icons/tb';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button as ButtonScroll, scroller } from 'react-scroll';
import SelectDate from '../../../components/Form/SelectDate';
import ModalLoading from '../../../components/Loader/ModalLoader';
import { trackDetailItem } from '../../../helper/utils/eventTrackGA';
import { formattedCurrency } from '../../../helper/utils/formattedCurrency';
import { formattedDate } from '../../../helper/utils/formattedDate';
import { urlNavigate } from '../../../helper/utils/navigation';
import { doHandleState } from '../../../redux/onlineSales/onlineSales.actions';
import useOnlineSales from '../hooks/useOnlineSales';
import ModalLogIn from './ModalLogIn';
import ProductCarts from './ProductCarts';

export default function ProductsOverview({ id = 'product', products = [], overview = {}, type = '', isLoading = false, onGetProducts = () => {} }) {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const urlParams = useParams();
    const isVerifiedWaiver = (localStorage.getItem('isVerifiedWaiver') || 'false') === 'true';

    const { selectedDate, handleSelectDate, outlets, outlet, isCartContainDate } = useOnlineSales();
    const { isOpen, onClose, onOpen } = useDisclosure();
    const { isOpen: isOpenVerify, onClose: onCloseVerify, onOpen: onOpenVerify } = useDisclosure();
    const [params, setParams] = useSearchParams();
    const { pathname } = useLocation();

    const productId = useMemo(() => urlParams?.productId ?? '', [urlParams]);
    const isScrollToProduct = useMemo(() => (params.get('isScrollToProduct') || 'false') === 'true', [params]);
    const handleChangeOutlet = (selectedOutlet) => {
        const brandId = (urlParams['brandId'] || '').toString().trim().toLowerCase();
        const outletCode = (selectedOutlet['outletName'] || '').toString().trim().toLowerCase();

        localStorage.setItem('outlet', JSON.stringify(selectedOutlet));
        dispatch(doHandleState('cart', []));

        navigate(`/${brandId}/${outletCode}/`);
    };

    useEffect(() => {
        onClose();
    }, [id]);

    useEffect(() => {
        onGetProducts();
    }, [type, productId]);

    useEffect(() => {
        if (isScrollToProduct) {
            scroller.scrollTo('product-list', {
                duration: 500,
                delay: 100,
                smooth: true,
                offset: -100,
            });
        }
    }, [isScrollToProduct]);

    return (
        <>
            <ModalLoading loading={isLoading} />
            <Container maxW='7xl' my={16} p={0}>
                <Box id={id} key={id}>
                    {/* Membership Banner - Only show to non-members */}
                    {pathname === `${urlNavigate('online-sales', 'home')}` && (
                        <Box w='full' bgGradient='linear(135deg, primary.500 0%, primary.600 50%, primary.700 100%)' rounded='2xl' overflow='hidden' position='relative' mb={8} boxShadow='xl'>
                            {/* Decorative background pattern */}
                            <Box position='absolute' top='-50%' right='-10%' w='300px' h='300px' bg='whiteAlpha.100' rounded='full' filter='blur(60px)' />
                            <Box position='absolute' bottom='-30%' left='-5%' w='200px' h='200px' bg='whiteAlpha.100' rounded='full' filter='blur(50px)' />

                            <Flex position='relative' zIndex={1} p={{ base: 6, md: 8 }} gap={{ base: 4, md: 6 }} alignItems='center' flexDirection={{ base: 'column', md: 'row' }}>
                                {/* Icon Section */}
                                <Box bg='whiteAlpha.200' backdropFilter='blur(10px)' p={4} rounded='xl' border='2px' borderColor='whiteAlpha.300' display={{ base: 'none', md: 'flex' }}>
                                    <Icon as={PiShoppingBag} fontSize={{ base: '40px', md: '50px' }} color='white' />
                                </Box>

                                {/* Content Section */}
                                <Box flex={1} textAlign={{ base: 'center', md: 'left' }}>
                                    <Heading fontSize={{ base: 'md', md: '2xl' }} fontWeight={700} color='white' mb={2}>
                                        Unlock Exclusive Member Benefits! 🎉
                                    </Heading>
                                    <Text lineHeight={1.25} fontSize={{ base: '12px', md: 'md' }} color='whiteAlpha.900'>
                                        Join our membership program to enjoy special discounts, early access to new products, and exclusive member-only deals.
                                    </Text>
                                </Box>

                                {/* CTA Button */}
                                <Box>
                                    <Button
                                        size={{ base: 'sm', md: 'lg' }}
                                        r
                                        bg='white'
                                        color='primary.600'
                                        leftIcon={<FaCrown />}
                                        _hover={{
                                            bg: 'whiteAlpha.900',
                                            transform: 'translateY(-2px)',
                                            boxShadow: '2xl',
                                        }}
                                        _active={{
                                            transform: 'translateY(0)',
                                        }}
                                        transition='all 0.3s ease'
                                        fontWeight={600}
                                        px={8}
                                        onClick={() => {
                                            setParams({
                                                date: params.get('date') || '',
                                                productId: params.get('productId') || '',
                                                isShowDetail: (params.get('productId') || '') !== '',
                                                type: params.get('type') || '',
                                                callback: urlNavigate('dashboard', 'membership'),
                                            });

                                            if (!isVerifiedWaiver) {
                                                onOpenVerify();
                                                return;
                                            }

                                            navigate(urlNavigate('dashboard', 'membership'));
                                        }}
                                        boxShadow='lg'>
                                        Buy Membership
                                    </Button>
                                </Box>
                            </Flex>
                        </Box>
                    )}
                    <Grid
                        w='full'
                        gridTemplateColumns={{
                            base: 'repeat(1, 1fr)',
                            md: 'repeat(12, 1fr)',
                        }}
                        gap={5}>
                        <GridItem colSpan={{ base: 1, md: 8 }}>
                            <Box w={{ base: 'full', md: '80%' }} h='auto'>
                                <Heading fontSize={{ base: '2xl', md: '3xl' }} fontWeight={500}>
                                    {overview['title'] || ''}
                                </Heading>
                                <Text fontSize='md' lineHeight={1.35} mt={3}>
                                    {overview['desc'] || ''}
                                </Text>
                            </Box>
                            <Box w='full' bg='white' p={5} mt={8} rounded='xl'>
                                <Flex w='full' alignItems={{ base: 'start', lg: 'center' }} justifyContent='space-between' flexDirection={{ base: 'column', md: 'row' }} flexWrap='wrap' gap={5}>
                                    <Box w='max-content' maxW={{ base: 'full', md: 'max-content' }}>
                                        <Flex gap={3} alignItems='center'>
                                            <Box>
                                                <Avatar bg='primary.50' border='1px' borderColor='primary.100' color='primary.800' icon={<PiShoppingBag />} />
                                            </Box>
                                            <Box>
                                                <Text fontSize='14px' color='gray'>
                                                    Product
                                                </Text>
                                                <Text fontSize='15px' fontWeight={600} casing='uppercase'>
                                                    {type}
                                                </Text>
                                            </Box>
                                        </Flex>
                                    </Box>
                                    <Box w='max-content' maxW={{ base: 'full', md: 'max-content' }}>
                                        {outlets && outlets.length > 1 ? (
                                            <Menu>
                                                <MenuButton as={Button} variant='unstyled' rightIcon={<BiChevronDown />} display='flex' alignItems='center' w='full'>
                                                    <Flex gap={3} alignItems='center'>
                                                        <Box>
                                                            <Avatar bg='primary.50' border='1px' borderColor='primary.100' color='primary.800' icon={<HiOutlineMapPin />} />
                                                        </Box>
                                                        <Box textAlign='left'>
                                                            <Text fontSize='14px' color='gray'>
                                                                Select Outlet
                                                            </Text>
                                                            <Text fontSize='15px' fontWeight={600} casing='uppercase' noOfLines={1}>
                                                                {outlet['desc'] || outlet['outletName'] || 'Select Outlet'}
                                                            </Text>
                                                        </Box>
                                                    </Flex>
                                                </MenuButton>
                                                <MenuList>
                                                    <MenuItem isDisabled mb={3}>
                                                        <Text fontSize='sm' fontWeight={400} color='gray.700'>
                                                            Select Other Outlets
                                                        </Text>
                                                    </MenuItem>
                                                    <Divider />
                                                    {outlets?.map((o, index) => {
                                                        const isDisabled = o['outletCode'] === outlet['outletCode'];

                                                        if (isDisabled) {
                                                            return null;
                                                        }

                                                        return (
                                                            <MenuItem key={index} onClick={() => handleChangeOutlet(o)}>
                                                                <Text fontSize='md' fontWeight={500}>
                                                                    {o['desc'] || o['outletName'] || ''}
                                                                </Text>
                                                            </MenuItem>
                                                        );
                                                    })}
                                                </MenuList>
                                            </Menu>
                                        ) : (
                                            <Flex gap={3} alignItems='center'>
                                                <Box>
                                                    <Avatar bg='primary.50' border='1px' borderColor='primary.100' color='primary.800' icon={<HiOutlineMapPin />} />
                                                </Box>
                                                <Box textAlign='left'>
                                                    <Text fontSize='14px' color='gray'>
                                                        Current Outlet
                                                    </Text>
                                                    <Text fontSize='15px' fontWeight={600} casing='uppercase' noOfLines={1}>
                                                        {outlet['desc'] || outlet['outletName'] || 'Select Outlet'}
                                                    </Text>
                                                </Box>
                                            </Flex>
                                        )}
                                    </Box>
                                    {isCartContainDate() ? null : (
                                        <Box w='max-content' maxW={{ base: 'full', md: 'max-content' }}>
                                            <SelectDate
                                                value={selectedDate}
                                                isOpen={isOpen}
                                                onClose={onClose}
                                                minDate={new Date()}
                                                onChange={(value) => {
                                                    onClose();

                                                    const formattedDate = new Date(value);
                                                    handleSelectDate(formattedDate);
                                                }}>
                                                <Button variant='unstyled' w='full' onClick={onOpen}>
                                                    <Flex gap={3} alignItems='start' justifyContent='start'>
                                                        <Box>
                                                            <Avatar bg='primary.50' border='1px' borderColor='primary.100' color='primary.800' icon={<LuCalendarSearch />} />
                                                        </Box>
                                                        <Box>
                                                            <Text fontSize='14px' color='gray' textAlign='start'>
                                                                Select Date
                                                            </Text>
                                                            <Text fontSize='15px' fontWeight={600} textAlign='start' casing='uppercase'>
                                                                {formattedDate(selectedDate)}
                                                            </Text>
                                                        </Box>
                                                    </Flex>
                                                </Button>
                                            </SelectDate>
                                        </Box>
                                    )}
                                    <Box w={isCartContainDate() ? 'max-content' : 'full'} mt={{ base: 7, md: 0 }}>
                                        <Button
                                            w='full'
                                            px={7}
                                            py={6}
                                            isLoading={isLoading}
                                            fontSize='14px'
                                            rounded='xl'
                                            leftIcon={<BiSearch />}
                                            colorScheme='primary'
                                            as={ButtonScroll}
                                            to='product-list'
                                            smooth
                                            delay={200}
                                            offset={-100}
                                            onClick={() => {
                                                onGetProducts();

                                                if (typeof overview.cta === 'function') {
                                                    overview.cta();
                                                }
                                            }}>
                                            Confirm Your Date
                                        </Button>
                                    </Box>
                                </Flex>
                            </Box>
                            <Box w='full' id='product-list' key='product-list'>
                                {isLoading || products.length == 0 ? (
                                    <Box w='full' h='350px' bg='white' p={5} mt={8} rounded='xl' display='flex' alignItems='center' justifyContent='center' flexDirection='column'>
                                        <Icon as={TbShoppingBagX} fontSize='50px' color='gray' />
                                        <Text mt={2} color='gray' textAlign='center' fontSize={{ base: 'md', md: 'lg' }}>
                                            Products Not Found
                                        </Text>
                                    </Box>
                                ) : (
                                    products?.map(({ pageName = '', data = [] }) => {
                                        return (
                                            <Box w='full' bg='white' p={5} mt={8} rounded='xl'>
                                                <Box>
                                                    <Heading fontSize={{ base: 'xl', md: '2xl' }} fontWeight={500} textTransform='capitalize'>
                                                        {pageName.trim().toLowerCase()}
                                                    </Heading>
                                                </Box>
                                                <Box w='full' h='auto' mt={7}>
                                                    {data?.map((item, index) => {
                                                        const image = item['imageUrl'].trim();
                                                        const combo = item['combo'] || [];
                                                        const mappingPrice = combo?.map((a) => parseFloat(a['basePrice'] || '0'));
                                                        const combosPrice = _.sum(mappingPrice);
                                                        const basePriceItem = parseFloat(item['basePrice']);
                                                        const price = basePriceItem - combosPrice;
                                                        const menuType = item?.menuType ?? '';
                                                        const isMenuParty = menuType === 'party';

                                                        return (
                                                            <Box key={index} mb={5}>
                                                                <Flex w='full' h='max-content' bg='white' alignItems='center' justifyContent='start' gap={4}>
                                                                    <Skeleton isLoaded={!isLoading}>
                                                                        <Box
                                                                            w={{
                                                                                base: '125px',
                                                                                md: '175px',
                                                                            }}
                                                                            h={{
                                                                                base: '75px',
                                                                                md: '100px',
                                                                            }}>
                                                                            <Center w='full' h='full' bg={image != '' ? 'transparent' : 'gray.50'} rounded='lg' overflow='hidden'>
                                                                                {image != '' ? <Image src={image} w='full' h='full' fit='contain' /> : <Icon fontSize='35px' color='gray.400' as={PiImageBroken} />}
                                                                            </Center>
                                                                        </Box>
                                                                    </Skeleton>
                                                                    <Box w='full'>
                                                                        <Flex
                                                                            w='full'
                                                                            flexWrap={{
                                                                                base: 'wrap',
                                                                                md: 'nowrap',
                                                                            }}
                                                                            justifyContent='space-between'
                                                                            alignItems='center'
                                                                            gap={5}>
                                                                            <Box w='full'>
                                                                                <Skeleton w='full' isLoaded={!isLoading}>
                                                                                    <Heading
                                                                                        fontWeight={500}
                                                                                        fontSize={{
                                                                                            base: '15px',
                                                                                            md: 'lg',
                                                                                        }}>
                                                                                        {item['onlineName'] ?? item['menuName'] ?? ''}
                                                                                    </Heading>
                                                                                </Skeleton>
                                                                                {!isMenuParty && (
                                                                                    <Skeleton w='100%' isLoaded={!isLoading}>
                                                                                        <Text
                                                                                            mt={1}
                                                                                            fontSize='13.5px'
                                                                                            color='gray.500'
                                                                                            casing='capitalize'
                                                                                            whiteSpace='normal'
                                                                                            noOfLines={{
                                                                                                base: 1,
                                                                                                md: 1,
                                                                                            }}>
                                                                                            {item['menuDescription'].trim().toLowerCase()}
                                                                                        </Text>
                                                                                    </Skeleton>
                                                                                )}
                                                                                <Skeleton w='50%' isLoaded={!isLoading}>
                                                                                    <Text
                                                                                        fontSize={{
                                                                                            base: 'sm',
                                                                                            md: 'md',
                                                                                        }}
                                                                                        fontWeight={600}
                                                                                        mt={3}>
                                                                                        {formattedCurrency(price)}
                                                                                    </Text>
                                                                                </Skeleton>
                                                                            </Box>
                                                                            <Box mt={{ base: 4, md: 0 }}>
                                                                                <Skeleton isLoaded={!isLoading}>
                                                                                    <Button
                                                                                        isLoading={isLoading}
                                                                                        variant='solid'
                                                                                        colorScheme='primary'
                                                                                        mt={{ base: 3, md: 0 }}
                                                                                        onClick={() => {
                                                                                            const state = {
                                                                                                pageName,
                                                                                                productType: type,
                                                                                                productId: item['menuCode'] || '',
                                                                                            };

                                                                                            const menuType = item?.menuType || '';
                                                                                            const isMenuParty = menuType == 'party';

                                                                                            trackDetailItem({
                                                                                                value: price,
                                                                                                currency: 'IDR',
                                                                                                items: [
                                                                                                    {
                                                                                                        item_id: item['menuCode'] || '',
                                                                                                        item_name: item['menuName'] || '',
                                                                                                        item_category: pageName,
                                                                                                        price: price,
                                                                                                        quantity: 1,
                                                                                                    },
                                                                                                ],
                                                                                            });

                                                                                            navigate(urlNavigate('online-sales', `${type}/${item['menuCode']}?callback=${isMenuParty ? urlNavigate('online-sales', 'checkout') : window.location.pathname}`), {
                                                                                                state,
                                                                                            });
                                                                                        }}
                                                                                        leftIcon={<BsCart4 fontSize='18px' />}
                                                                                        fontSize={{
                                                                                            base: '12px',
                                                                                            md: 'sm',
                                                                                        }}>
                                                                                        Add to Cart
                                                                                    </Button>
                                                                                </Skeleton>
                                                                            </Box>
                                                                        </Flex>
                                                                    </Box>
                                                                </Flex>
                                                            </Box>
                                                        );
                                                    })}
                                                </Box>
                                            </Box>
                                        );
                                    })
                                )}
                            </Box>
                        </GridItem>
                        <GridItem colSpan={{ base: 1, md: 4 }}>
                            <ProductCarts />
                        </GridItem>
                    </Grid>
                </Box>
            </Container>

            <ModalLogIn isOpen={isOpenVerify} onClose={onCloseVerify} />
        </>
    );
}
